import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    Paper,
    Typography,
    Box,
    Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { sectionTitleSx } from '../styles/typography';
import { negativeCell } from '../styles/tailwindClasses';
import { selectTickerExposureRows } from '../utils/riskOverviewChartPresentation';
import {
    SecurityIdentityFieldMenu,
    securityIdentityFieldLabel,
} from './commonComponents/SecurityIdentityFieldSelector';
import { securityIdentityPresentation } from '../utils/securityIdentityPresentation';
import DataTable from './data_table/DataTable';
import {
    MSI_GEOMETRY,
    APP_STYLE_PALETTE as MSI_PALETTE,
    usesAppStyleGrammar,
    resolveMsiSxRadius,
} from '../styles/msiStyleTokens';

const getTickerRowId = (row, index) => row.rowKey || `row_${index}`;

// A right-aligned number column; negative values are red.
function numberColumn({ id, header, format }) {
    return {
        id,
        header,
        accessorFn: (row) => row[id],
        cell: ({ getValue }) => format(getValue()),
        meta: {
            align: 'right',
            cellClassName: ({ getValue }) => {
                const value = getValue();
                return typeof value === 'number' && value < 0 ? negativeCell : undefined;
            },
        },
    };
}

const formatPercent = (value) => (typeof value === 'number' ? `${value.toFixed(2)}%` : '-');
const NUMBER_COLUMNS = Object.freeze([
    numberColumn({ id: 'exposure', header: 'Exposure', format: (value) => Math.round(value).toLocaleString() }),
    numberColumn({ id: 'factorVarPct', header: '% Var', format: formatPercent }),
    numberColumn({ id: 'expPerNmv', header: 'Exp / NMV', format: (value) => (value == null ? '-' : value.toFixed(4)) }),
    numberColumn({ id: 'pctExp', header: '% Exp', format: (value) => (value == null ? '-' : `${value.toFixed(2)}%`) }),
]);

function TickerExposureTable({
    factorName,
    tickers = [],
    isLoading = false,
    error = '',
    hideTitle = false,
    withPaper = true,
    maxHeight = 400,
    containerSx = {},
}) {
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('exposure');
    const [identityDisplayField, setIdentityDisplayField] = useState('company_name');

    const sortedTickers = useMemo(() => {
        return selectTickerExposureRows({ tickers, order, orderBy, identityDisplayField });
    }, [tickers, order, orderBy, identityDisplayField]);
    // selectTickerExposureRows sorts; a header click toggles its column
    // between ascending and descending (ascending first), never unsorted.
    const sorting = useMemo(() => [{ id: orderBy, desc: order === 'desc' }], [order, orderBy]);
    const handleSortingChange = (nextSorting) => {
        const [next] = nextSorting;
        setOrderBy(next ? next.id : orderBy);
        setOrder(next?.desc ? 'desc' : 'asc');
    };
    const columns = useMemo(() => [
        {
            id: 'security',
            header: securityIdentityFieldLabel(identityDisplayField),
            accessorFn: (row) => securityIdentityPresentation(row, identityDisplayField).value,
            cell: ({ row: { original } }) => {
                const identity = securityIdentityPresentation(original, identityDisplayField);
                return <span title={identity.tooltip || identity.value}>{identity.value}</span>;
            },
            meta: {
                headerControls: () => (
                    <SecurityIdentityFieldMenu value={identityDisplayField} onChange={setIdentityDisplayField} />
                ),
            },
        },
        ...NUMBER_COLUMNS,
    ], [identityDisplayField]);

    // While loading, the table shows its placeholder rows.
    if (!isLoading && error) {
        return (
            <Alert severity="error" sx={{ mt: 4 }}>
                {error}
            </Alert>
        );
    }

    if (!isLoading && sortedTickers.length === 0) {
        return (
            <Typography
                variant="caption"
                sx={{
                    mt: 2,
                    display: 'block',
                    textAlign: 'center',
                    color: 'text.secondary',
                    fontStyle: 'italic',
                    p: 2,
                    backgroundColor: (theme) => (
                        usesAppStyleGrammar(theme)
                            ? MSI_PALETTE.surfaceSubtle
                            : alpha(theme.palette.text.primary, 0.05)
                    ),
                    borderRadius: (theme) => resolveMsiSxRadius(
                        theme,
                        MSI_GEOMETRY.radii.iconButton,
                        1,
                    ),
                    width: '100%'
                }}
            >
                No securities found for the selected factor.
            </Typography>
        );
    }

    const ContainerComponent = withPaper ? Paper : Box;

    return (
        <ContainerComponent
            sx={{
                mt: hideTitle ? 0 : 1,
                p: withPaper ? 1 : 0,
                width: 'fit-content',
                maxWidth: '100%',
                borderRadius: (theme) => (
                    withPaper
                        ? resolveMsiSxRadius(theme, MSI_GEOMETRY.radii.panel, 1.5)
                        : 0
                ),
                boxShadow: 'none',
                ...containerSx,
            }}
        >
            {!hideTitle && (
                <Typography
                    variant="subtitle2"
                    align="center"
                    sx={{ ...sectionTitleSx, px: 2, py: 1.5 }}
                >
                    {factorName}
                </Typography>
            )}
            <DataTable
                aria-label={`Securities exposed to ${factorName}`}
                data={sortedTickers}
                columns={columns}
                loading={isLoading}
                getRowId={getTickerRowId}
                sorting={sorting}
                onSortingChange={handleSortingChange}
                manualSorting
                tableLayout="auto"
                maxHeight={maxHeight}
                className="w-fit max-w-full"
                rowHeight={32}
                headerHeight={36}
            />
        </ContainerComponent>
    );
}

TickerExposureTable.propTypes = {
    factorName: PropTypes.string.isRequired,
    tickers: PropTypes.arrayOf(
        PropTypes.shape({
            cognive_id: PropTypes.string,
            display_sedol: PropTypes.string,
            sedol: PropTypes.string,
            inbound_id_type: PropTypes.string,
            inbound_id_value: PropTypes.string,
            ticker: PropTypes.string,
            exposure: PropTypes.number.isRequired,
            factorVarPct: PropTypes.number,
            nmv: PropTypes.number,
        })
    ),
    isLoading: PropTypes.bool,
    error: PropTypes.string,
    hideTitle: PropTypes.bool,
    withPaper: PropTypes.bool,
    maxHeight: PropTypes.number,
    containerSx: PropTypes.object,
};

export default TickerExposureTable;
